import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';

import { useDataTable, useTableSearchCondition } from '@kc-console/shared-ui';

import {
  dateQuerySyncFilters,
  isDateQueryFilter,
  replaceDateQueryFilters,
  validateDateQueryFilters,
} from '../model/dateQueryFilters';
import { DEFAULT_PERIOD_PRESET, getPresetDateRange, toApiDate } from '../model/period';
import useGetConsoleEvents from '../model/useGetConsoleEvents';
import useEventColumns from './useEventColumns';
import useEventFilters from './useEventFilters';
import useEventsRequestParams from './useEventsRequestParams';

import type { PropsWithChildren } from 'react';
import type { DataTableProps, FilterState } from '@kc-console/shared-ui';
import type { DateQueryFilterKey } from '../model/dateQueryFilters';
import type { PeriodPreset } from '../model/period';
import type { EventItem } from '../model/types';

const DEFAULT_PAGE_SIZE = 20;

type EventBannerState = {
  dataUpdatedAt: number;
  isError: boolean;
  unsolvedEventCount: number;
};

type EventDateFilterState = {
  customRange: [Date | null, Date | null];
  onCustomRangeChange: (range: [Date | null, Date | null]) => void;
  onPresetChange: (preset: PeriodPreset) => void;
  periodPreset: PeriodPreset;
};

type EventsControllerContextValue = {
  bannerState: EventBannerState;
  dateFilter: EventDateFilterState;
  tableProps: Pick<
    DataTableProps<EventItem>,
    | 'filter'
    | 'isError'
    | 'isFetching'
    | 'isLoading'
    | 'onRefetch'
    | 'pagination'
    | 'sorting'
    | 'table'
  >;
};

const EventsControllerContext = createContext<EventsControllerContextValue | null>(null);

/**
 * 이벤트 내역 페이지의 목록 상태와 서버 조회 상태를 관리한다.
 * @description
 * - ListSection header/body가 같은 query 결과를 공유하도록 이벤트 페이지 상태를 관리
 * - header에서는 미해결 이벤트 배너 상태사용. body에서는 DataTable 상태사용.
 */
export default function EventsControllerProvider({ children }: PropsWithChildren) {
  const columns = useEventColumns();
  const visibleFilters = useEventFilters();

  // 날짜 query는 화면 필터가 아니지만 useTableSearchCondition의 query sync에 포함시켜야 한다.
  const querySyncFilters = useMemo(
    () => [...visibleFilters, ...dateQuerySyncFilters],
    [visibleFilters],
  );

  const {
    filterState,
    setFilterState,
    pageIndex,
    pageSize,
    setPageIndex,
    setPageSize,
    sortState,
    setSortState,
    tableSettings,
    setTableSettings,
    searchCondition,
  } = useTableSearchCondition({
    tableKey: 'events-table',
    filters: querySyncFilters,
    enableQuerySync: { filter: true, page: true, sort: true },
    initialState: {
      page: { pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE },
    },
  });

  const visibleFilterState = useMemo(
    () => filterState.filter(filter => !isDateQueryFilter(filter)),
    [filterState],
  );

  // 사용자가 URL query를 임의 수정한 경우 기본 프리셋으로 렌더링한 뒤 query를 정리한다.
  const dateQueryValidation = useMemo(() => validateDateQueryFilters(filterState), [filterState]);

  const periodPreset = useMemo<PeriodPreset>(() => {
    if (!dateQueryValidation.isValid) {
      return DEFAULT_PERIOD_PRESET;
    }

    return dateQueryValidation.periodPreset ?? DEFAULT_PERIOD_PRESET;
  }, [dateQueryValidation]);

  const customRange = useMemo<[Date | null, Date | null]>(
    () => (dateQueryValidation.isValid ? dateQueryValidation.customRange : [null, null]),
    [dateQueryValidation],
  );

  const refinedParams = useEventsRequestParams(searchCondition, { periodPreset, customRange });

  const handleVisibleFiltersChange = useCallback(
    (nextVisibleFilters: FilterState[]) => {
      const dateQueryFilters = filterState.filter(isDateQueryFilter);
      setFilterState([...nextVisibleFilters, ...dateQueryFilters]);
    },
    [filterState, setFilterState],
  );

  const handlePresetChange = useCallback(
    (preset: PeriodPreset) => {
      const nextDateQuery: Partial<Record<DateQueryFilterKey, string | undefined>> = {
        periodPreset: preset,
      };

      // '사용자화' 진입 시 직전 프리셋의 조회 기간을 그대로 선택 상태로 이어받는다.
      if (preset === 'custom') {
        const prevRange = getPresetDateRange(periodPreset);
        if (prevRange) {
          nextDateQuery.fromDate = toApiDate(prevRange[0]);
          nextDateQuery.toDate = toApiDate(prevRange[1]);
        }
      } else {
        nextDateQuery.fromDate = undefined;
        nextDateQuery.toDate = undefined;
      }

      setFilterState(replaceDateQueryFilters(filterState, nextDateQuery));
      setPageIndex(1);
    },
    [periodPreset, setPageIndex, filterState, setFilterState],
  );

  const handleCustomRangeChange = useCallback(
    (range: [Date | null, Date | null]) => {
      setFilterState(
        replaceDateQueryFilters(filterState, {
          periodPreset: 'custom',
          fromDate: range[0] ? toApiDate(range[0]) : undefined,
          toDate: range[1] ? toApiDate(range[1]) : undefined,
        }),
      );
      setPageIndex(1);
    },
    [filterState, setFilterState, setPageIndex],
  );

  useEffect(() => {
    if (dateQueryValidation.isValid) {
      return;
    }

    // invalid 날짜 query 제거 시 DataTable auto-reset이 건드린 page/sort는 기존 값으로 되돌린다.
    setFilterState(
      replaceDateQueryFilters(filterState, {
        periodPreset: undefined,
        fromDate: undefined,
        toDate: undefined,
      }),
    );
    setSortState(sortState);
    setPageIndex(pageIndex);
  }, [
    dateQueryValidation.isValid,
    filterState,
    pageIndex,
    setFilterState,
    setPageIndex,
    setSortState,
    sortState,
  ]);

  const { data, dataUpdatedAt, isLoading, isError, isFetching, refetch } = useGetConsoleEvents({
    ...refinedParams,
    page: pageIndex - 1,
    limit: pageSize,
  });

  const content = useMemo(() => data?.eventList?.content ?? [], [data?.eventList?.content]);
  const totalCount = data?.eventList?.total ?? 0;

  const { tableInstance } = useDataTable({
    columns,
    data: content,
    getRowId: 'eventId',
    enableRowSelection: false,
    tableSettings,
    onTableSettingsChange: setTableSettings,
  });

  const value = useMemo<EventsControllerContextValue>(
    () => ({
      bannerState: {
        unsolvedEventCount: data?.unsolvedEventCount ?? 0,
        dataUpdatedAt,
        isError,
      },
      dateFilter: {
        periodPreset,
        customRange,
        onPresetChange: handlePresetChange,
        onCustomRangeChange: handleCustomRangeChange,
      },
      tableProps: {
        table: tableInstance,
        isLoading,
        isError,
        isFetching,
        onRefetch: refetch,
        filter: {
          filterDefs: visibleFilters,
          filters: visibleFilterState,
          onFiltersChange: handleVisibleFiltersChange,
        },
        sorting: {
          sorting: sortState,
          onSortingChange: setSortState,
        },
        pagination: {
          totalCount,
          pageIndex,
          pageSize,
          onPageIndexChange: setPageIndex,
          onPageSizeChange: setPageSize,
        },
      },
    }),
    [
      customRange,
      data?.unsolvedEventCount,
      dataUpdatedAt,
      handleCustomRangeChange,
      handlePresetChange,
      handleVisibleFiltersChange,
      isError,
      isFetching,
      isLoading,
      pageIndex,
      pageSize,
      periodPreset,
      refetch,
      setPageIndex,
      setPageSize,
      setSortState,
      sortState,
      tableInstance,
      totalCount,
      visibleFilterState,
      visibleFilters,
    ],
  );

  return (
    <EventsControllerContext.Provider value={value}>{children}</EventsControllerContext.Provider>
  );
}

export function useEventsController() {
  const context = useContext(EventsControllerContext);

  if (!context) {
    throw new Error('EventsControllerProvider is not provided');
  }

  return context;
}

export function useEventsTableProps() {
  return useEventsController().tableProps;
}

export function useEventsDateFilter() {
  return useEventsController().dateFilter;
}
